package com.tovtalk.presentation.viewmodel.onboarding

import com.tovtalk.domain.entity.Category
import com.tovtalk.domain.entity.DomainResult
import com.tovtalk.domain.entity.KosherLevel
import com.tovtalk.data.local.DiscoverCache
import com.tovtalk.domain.repository.AppRepository
import com.tovtalk.domain.repository.PostRepository
import com.tovtalk.domain.repository.SettingsRepository
import com.tovtalk.domain.repository.UserRepository
import com.tovtalk.presentation.ViewModel
import com.tovtalk.presentation.ScopedViewModel
import kotlinx.coroutines.cancel
import com.tovtalk.presentation.state.UiState
import com.tovtalk.utils.logger.Logger
import com.tovtalk.utils.notifications.requestNotificationPermissionIfNeeded
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch

/**
 * Five-step guest onboarding flow:
 * Welcome -> Notifications -> Kashrut -> Categories -> Done
 *
 * On Done, persists the selections to the server via UserRepository.continueAsGuest,
 * which issues the guest token and stores it in TokenManager.
 */
enum class OnboardingStep { Welcome, Notifications, Kashrut, Categories, Done }

data class OnboardingState(
    val step: OnboardingStep = OnboardingStep.Welcome,
    val kashrutLevels: List<KosherLevel> = emptyList(),
    val categories: List<Category> = emptyList(),
    val selectedKashrutLevelId: Int? = null,
    val selectedCategoryIds: Set<Int> = emptySet(),
    val loading: Boolean = false,
    val error: String? = null,
)

class OnboardingViewModel(
    private val userRepository: UserRepository,
    private val appRepository: AppRepository,
    private val settingsRepository: SettingsRepository,
    private val postRepository: PostRepository,
    private val scope: CoroutineScope,
) : ViewModel(), ScopedViewModel {

    /** Cancels the injected scope + all launched jobs when onboarding leaves. */
    override fun dispose() {
        scope.cancel()
    }


    private val _state = MutableStateFlow(OnboardingState())
    val state: StateFlow<OnboardingState> = _state.asStateFlow()

    private val _commitState = MutableStateFlow<UiState<Unit>>(UiState.Idle)
    val commitState: StateFlow<UiState<Unit>> = _commitState.asStateFlow()

    /**
     * Load kashrut levels and categories. Call when the onboarding screen enters composition.
     */
    fun start() {
        scope.launch {
            _state.value = _state.value.copy(loading = true, error = null)

            val levelsResult = appRepository.getKosherLevels(hadranOnly = false)
            val categoriesResult = appRepository.getCategories()

            val levels = when (levelsResult) {
                is DomainResult.Success -> levelsResult.data
                is DomainResult.Error -> {
                    Logger.e("getKosherLevels failed: ${levelsResult.message}", "OnboardingViewModel")
                    emptyList()
                }
                is DomainResult.Loading -> emptyList()
            }
            val categories = when (categoriesResult) {
                is DomainResult.Success -> categoriesResult.data
                is DomainResult.Error -> {
                    Logger.e("getCategories failed: ${categoriesResult.message}", "OnboardingViewModel")
                    emptyList()
                }
                is DomainResult.Loading -> emptyList()
            }

            val errorMessage = when {
                levelsResult is DomainResult.Error -> levelsResult.message
                categoriesResult is DomainResult.Error -> categoriesResult.message
                else -> null
            }

            // Default to the lowest-order kashrut level when none is selected.
            val currentSelection = _state.value.selectedKashrutLevelId
            val defaultLevelId = currentSelection ?: levels.minByOrNull { it.order }?.id

            _state.value = _state.value.copy(
                kashrutLevels = levels,
                categories = categories,
                selectedKashrutLevelId = defaultLevelId,
                loading = false,
                error = errorMessage,
            )
        }
    }

    /** Advance to the next step. Commits the guest session when moving into Done. */
    fun advance() {
        val current = _state.value.step
        val next = when (current) {
            OnboardingStep.Welcome -> OnboardingStep.Notifications
            OnboardingStep.Notifications -> OnboardingStep.Kashrut
            OnboardingStep.Kashrut -> OnboardingStep.Categories
            OnboardingStep.Categories -> OnboardingStep.Done
            OnboardingStep.Done -> OnboardingStep.Done
        }
        _state.value = _state.value.copy(step = next)

        // Start prefetching feed as soon as user selected kashrut (moving to Categories)
        if (current == OnboardingStep.Kashrut && next == OnboardingStep.Categories) {
            prefetchFeed()
        }

        if (next == OnboardingStep.Done && current != OnboardingStep.Done) {
            commitGuestSession()
        }
    }

    /** Skip the current step. Same state machine semantics as advance. */
    fun skip() = advance()

    /**
     * Step back one position. Returns true when the step was changed so callers can
     * consume the back press; returns false on Welcome/Done so the system default
     * (e.g. closing the app) takes over.
     */
    fun goBack(): Boolean {
        val current = _state.value.step
        val prev = when (current) {
            OnboardingStep.Welcome -> return false
            OnboardingStep.Notifications -> OnboardingStep.Welcome
            OnboardingStep.Kashrut -> OnboardingStep.Notifications
            OnboardingStep.Categories -> OnboardingStep.Kashrut
            OnboardingStep.Done -> return false
        }
        _state.value = _state.value.copy(step = prev)
        return true
    }

    fun selectKashrut(id: Int) {
        _state.value = _state.value.copy(selectedKashrutLevelId = id)
    }

    fun toggleCategory(id: Int) {
        val current = _state.value.selectedCategoryIds
        val next = if (id in current) current - id else current + id
        _state.value = _state.value.copy(selectedCategoryIds = next)
    }

    /**
     * Trigger the platform notification permission prompt. No-op on platforms
     * where permission is handled elsewhere (e.g. Android MainActivity).
     */
    fun requestNotifications(onResult: (granted: Boolean) -> Unit = {}) {
        requestNotificationPermissionIfNeeded(onResult)
    }

    /** Re-runs the guest session commit. Called from the error dialog's "נסה שוב". */
    fun retryCommit() {
        if (_commitState.value is UiState.Loading) return
        commitGuestSession()
    }

    /**
     * Cancels the commit and returns the user to the Categories step with their
     * selections intact, so they can tap "סיום" again.
     */
    fun cancelCommit() {
        _commitState.value = UiState.Idle
        _state.value = _state.value.copy(
            step = OnboardingStep.Categories,
            error = null,
        )
    }

    /**
     * Background prefetch: load 2 discover videos so Home has content immediately.
     * Uses whatever guest token is available (created in SplashScreen).
     * Runs silently — errors are logged but don't affect onboarding.
     */
    private fun prefetchFeed() {
        scope.launch {
            Logger.d("Onboarding: prefetching feed", "OnboardingViewModel")
            when (val result = postRepository.getDiscoverFeed(cursor = null, limit = 2)) {
                is DomainResult.Success -> {
                    DiscoverCache.setFirstPage(result.data.posts)
                    Logger.d("Onboarding: prefetched ${result.data.posts.size} posts", "OnboardingViewModel")
                }
                is DomainResult.Error -> {
                    Logger.d("Onboarding: feed prefetch failed: ${result.message}", "OnboardingViewModel")
                }
                else -> {}
            }
        }
    }

    private fun commitGuestSession() {
        scope.launch {
            _commitState.value = UiState.Loading
            val s = _state.value
            val categoryIds = s.selectedCategoryIds.toList().takeIf { it.isNotEmpty() }

            val backoffsMs = longArrayOf(500L, 1000L) // attempts 2 and 3 wait these before firing
            var lastError: String? = null

            repeat(MAX_COMMIT_ATTEMPTS) { attemptIndex ->
                if (attemptIndex > 0) {
                    delay(backoffsMs[attemptIndex - 1])
                }
                when (val result = userRepository.continueAsGuest(s.selectedKashrutLevelId, categoryIds)) {
                    is DomainResult.Success -> {
                        settingsRepository.setKashrutSelected()
                        Logger.d("Guest onboarding committed (attempt ${attemptIndex + 1})", "OnboardingViewModel")
                        _commitState.value = UiState.Success(Unit)
                        return@launch
                    }
                    is DomainResult.Error -> {
                        Logger.e(
                            "continueAsGuest failed (attempt ${attemptIndex + 1}/$MAX_COMMIT_ATTEMPTS): ${result.message}",
                            "OnboardingViewModel"
                        )
                        lastError = result.message
                    }
                    is DomainResult.Loading -> {
                        // Shouldn't happen for a suspend call — treat as error for retry purposes.
                        lastError = "Unexpected loading state from continueAsGuest"
                    }
                }
            }

            _commitState.value = UiState.Error(lastError ?: "Unknown error")
        }
    }

    companion object {
        private const val MAX_COMMIT_ATTEMPTS = 3
    }
}
